import React from 'react';
import 'twin.macro';
import { StaticImage } from 'gatsby-plugin-image';
import { Link } from 'gatsby';

import Layout from '~components/Layout';
import SEO from '~components/SEO';
import Card, { IconCardLink } from '~components/Card';
import Button from '~components/Button';
import FadedImage, { fadedImageStyle, fadedStyle } from '~components/FadedImage';
import Heading from '~components/Heading';
import Text, { Bold } from '~components/Text';
import TitleBar from '~components/TitleBar';

// SVGs
import Beaker from '~components/Svg/Beaker';
import CurvedArrowLeft from '~components/Svg/CurvedArrowLeft';
import GlobeBadge from '~components/Svg/GlobeBadge';
import GroupTalking from '~components/Svg/GroupTalking';
import Microscope from '~components/Svg/Microscope';
import MoneyPot from '~components/Svg/MoneyPot';
import PaperAirplaneLeft from '~components/Svg/PaperAirplaneLeft';
import PaperAirplaneRight from '~components/Svg/PaperAirplaneRight';
import Pills from '~components/Svg/Pills';
import ResearchFolder from '~components/Svg/ResearchFolder';
import TestTube from '~components/Svg/TestTube';
import TwistArrowLeft from '~components/Svg/TwistArrowLeft';
import Wave from '~components/Svg/Wave';
import Handshake from '~components/Svg/Handshake';
import Government from '~components/Svg/Government';
import DocumentCheck from '~components/Svg/DocumentCheck';
import RoundTable from '~components/Svg/RoundTable';
import HandshakeSquare from '~components/Svg/HandshakeSquare';
import Tester from '~components/Svg/Tester';
import ComputerMoney from '~components/Svg/ComputerMoney';
import Champagne from '~components/Svg/Champagne';
import Bike from '~components/Svg/Bike';
import Shoe from '~components/Svg/Shoe';
import GradCap from '~components/Svg/GradCap';
import T1DSquare from '~components/Svg/T1DSquare';
import Puzzle from '~components/Svg/Puzzle';
import Bear from '~components/Svg/Bear';
import Podium from '~components/Svg/Podium';

// Sections
import BoardMembers from '~components/PageSection/BoardMembers';
import Leadership from '~components/PageSection/Leadership';
import Momentum from '~components/PageSection/Momentum';
import Partnerships from '~components/PageSection/Partnerships';
import ResearchGrants from '~components/PageSection/ResearchGrants';
import Stories from '~components/PageSection/Stories';
import SupportingEachother from '~components/PageSection/SupportingEachother';

import { fsbgImg } from '~theme';

const IndexPage: React.FC = () => (
  <Layout>
    <SEO />
    <div tw="bg-blue text-gray-50 py-12 relative overflow-y-visible relative z-10">
      <Wave tw="h-auto -mx-96" />
      <div tw="container py-12 relative z-20">
        <Heading level="h1" tw="flex flex-col">
          <span tw="text-9xl">Résilience</span>
          <span tw="text-5xl">VOTRE IMPACT 2020</span>
        </Heading>
      </div>
      <Wave tw="h-auto -mx-72 text-blue-600 opacity-60 absolute top-1/3 z-10 transform rotate-180" />
      <Wave tw="h-auto -mx-64 text-blue-600 opacity-60 absolute top-2/3 z-10" />
    </div>
    <div tw="relative z-20 -mr-32 hidden md:block w-full md:(-mt-48) lg:(-mt-64) xl:(-mt-72)">
      <StaticImage src="../images/people-inside-of-circles-large.png" alt="" layout="fullWidth" />
    </div>
    <div tw="container">
      <Bold>
      Merci pour votre soutien généreux. Vous nous aidez à nous rapprocher d'un monde sans diabète de type 1 (DT1).
      </Bold>
      <Text>
      FRDJ Canada a été fondée en 1974 par des familles touchées par le DT1, motivées par le besoin d'améliorer la qualité de vie non seulement de leurs enfants, mais aussi de toutes les personnes souffrant de cette maladie. Nous travaillons chaque jour pour honorer leur héritage et financer les recherches les plus prometteuses pour atteindre, un jour, leur objectif d'un monde sans diabète.
      </Text>
      <Text>
      FRDJ Canada est le principal bailleur de fonds caritatif de la recherche sur le DT1 au pays. Notre force réside dans notre concentration exclusive sur cette maladie et notre influence sur l'effort mondial d’y mettre fin. FRDJ Canada est fière de faire partie d'un réseau international d'affiliés travaillant avec la même vision : un monde sans diabète de type 1. Outre notre Fondation, il existe des filiales de FRDJ aux États-Unis, au Royaume-Uni, en Australie, aux Pays-Bas et en Israël.
      </Text>
      <div tw="relative my-36">
        <div tw="relative grid gap-6 items-start z-20 md:(grid-cols-2 gap-12) lg:(gap-24)">
          <Card>
            <Heading level="h2" tw="mt-0 mb-1">
              Vision
            </Heading>
            <Text tw="my-0">Un monde sans diabète de type 1</Text>
          </Card>
          <Card>
            <Heading level="h2" tw="mt-0 mb-1">
              Mission
            </Heading>
            <Text tw="my-0">
            Améliorer la vie d'aujourd'hui et de demain en accélérant les avancées décisives pour guérir, prévenir et traiter le DT1 et ses complications.
            </Text>
          </Card>
        </div>
        <CurvedArrowLeft tw="h-24 text-blue w-auto absolute left-full bottom-full -ml-24 mb-3 z-10 lg:(-ml-12)" />
        <PaperAirplaneRight tw="h-24 text-blue w-auto absolute right-full top-full -mr-24 mt-3 z-10  lg:(-mr-12)" />
      </div>
      <Text>
      Le DT1 touche jusqu'à 300 000 Canadien(ne)s et leurs familles. Il s'agit d'une maladie grave et difficile, qui doit être gérée avec vigilance au quotidien, et qui peut aussi entraîner des complications graves et potentiellement mortelles, notamment une maladie rénale, la cécité, des amputations, etc.
      </Text>
      <Text>
      Garder les niveaux de sucre et d'insuline sous contrôle signifie, pour les personnes vivant avec le DT1, de prendre plusieurs décisions quotidiennement. L'insuline est un traitement mais pas un remède, et le DT1 est actuellement la seule maladie auto-immune pour laquelle il n’existe pas de traitement modificateur sur le marché. La dépendance à des sources externes d'insuline expose les enfants à des risques de complications dangereuses, comme l'hyper ou l'hypoglycémie, qui peuvent mettre leur vie en danger.
      </Text>
      <Text>
      Le Canada est l'un des pays où les cas de DT1 augmentent le plus au monde, et nous ne savons pas pourquoi. Cela affecte à la fois les enfants et les adultes, l'âge médian du diagnostic se situant entre 10 et 14 ans, et des enfants aussi jeunes qu'un an peuvent en recevoir le diagnostic. Bien que la cause reste inconnue et ne puisse actuellement être évitée, les chercheurs se rapprochent de la compréhension des moyens de dépister puis d'empêcher la maladie de s'installer, nous rapprochant ainsi d'une guérison.
      </Text>
    </div>
    <FadedImage
      image={
        <StaticImage
          src="../images/black-person-researching.jpg"
          alt=""
          layout="fullWidth"
          style={fadedStyle}
          imgStyle={fadedImageStyle}
        />
      }
    >
      <Heading level="h2">Œuvrer pour un monde sans DT1</Heading>
      <Text tw="text-xl">
      FRDJ travaille chaque jour pour combattre cette maladie et changer la réalité des Canadiens qui en souffrent. 
      </Text>
      <Text tw="text-lg">Nous le faisons en :</Text>
      <ul tw="pl-6 list-outside list-disc text-base flex flex-col space-y-3">
        <li>
        Finançant des recherches révolutionnaires qui améliorent la vie grâce à de nouvelles thérapies qui arrivent sur le marché et se rapprochent de la découverte d’une guérison
        </li>
        <li>
        Sollicitant le soutien des gouvernements pour le financement de la recherche et l'élargissement de la couverture et de l'accès aux dispositifs DT1, tels que les pompes à insuline et les moniteurs de surveillance de la glycémie, permettant aux personnes souffrant de DT1 de mieux gérer leur maladie et de potentiellement prévenir les complications
        </li>
        <li>
        Éduquant la communauté du DT1 sur les enjeux liés à la maladie, ainsi qu'en mettant les communautés en relation les unes avec les autres pour partager et apprendre
        </li>
      </ul>
      <Text tw="text-base">
      Nous devons maintenir le rythme du financement afin que les progrès ne ralentissent pas ou ne s'arrêtent pas complètement, et nous pourrons être dans un monde sans diabète de type 1.
      </Text>
    </FadedImage>
    <div tw="container my-24">
      <div tw="grid gap-6 items-center lg:(grid-cols-2)">
        <div>
          <Heading level="h2" tw="mt-0">
          Message des dirigeants
          </Heading>
          <Text>
          Le président du conseil d’administration de FRDJ, Ron Miller, et le président et chef de la direction, Dave Prowten, discutent des activités et des réalisations de FRDJ en 2020, une année pas comme les autres. Malgré la pandémie et l’incertitude qu’elle a entraînée, FRDJ a adopté un sentiment de résilience et s’est tournée vers de nouvelles façons d’engager les partisans et les donateurs, tout en préparant notre nouvelle Campagne pour Accélérer et la commémoration du 100e anniversaire de la découverte de l’insuline.
          </Text>
          <Text>
            <Button variant="blue" to="/leadership-message">
            Lire le message complet des dirigeants 
            </Button>
          </Text>
        </div>
        <Leadership />
      </div>
    </div>
    <TitleBar icon={<TwistArrowLeft tw="h-24 w-auto text-blue" />}>Un soutien mutuel</TitleBar>
    <SupportingEachother />
    <TitleBar icon={<Microscope tw="h-24 w-auto text-blue" />}>Accélérer la recherche</TitleBar>
    <div tw="container">
      <ResearchGrants />
    </div>
    <Stories />
    <TitleBar icon={<PaperAirplaneLeft tw="h-24 w-auto text-blue" />}>Une initiative pour le changement</TitleBar>
    <div tw="container my-24">
      <Momentum />
    </div>
    <TitleBar icon={<Handshake tw="h-24 w-auto text-blue" />}>Partenaires nationaux</TitleBar>
    <div tw="container">
      <Partnerships />
    </div>
    <TitleBar icon={<GroupTalking tw="h-24 w-auto text-blue" />}>Conseil d’administration 2020</TitleBar>
    <div tw="container">
      <BoardMembers />
    </div>
    <TitleBar icon={<CurvedArrowLeft tw="h-24 w-auto text-blue" />}>Votre impact</TitleBar>
    <div tw="container">
      <div tw="grid gap-12 my-12 lg:(grid-cols-3 grid-rows-2)">
        <div tw="lg:(col-span-2 row-span-1)">
          <IconCardLink
            to="/your-impact/research"
            title="Recherche"
            icons={
              <>
                <GlobeBadge tw="h-12 w-auto" />
                <ResearchFolder tw="h-12 w-auto" />
                <TestTube tw="h-12 w-auto" />
                <Pills tw="h-12 w-auto" />
                <MoneyPot tw="h-12 w-auto" />
                <Beaker tw="h-12 w-auto" />
              </>
            }
          >
            <StaticImage
              src="../images/research-folder.jpg"
              alt=""
              style={fsbgImg}
              layout="fullWidth"
            />
          </IconCardLink>
        </div>
        <div tw="lg:(col-span-1 row-span-1)">
          <IconCardLink
            to="/your-impact/advocacy"
            title="Défense d’intérêts"
            icons={
              <>
                <Government tw="h-12 w-auto" />
                <DocumentCheck tw="h-12 w-auto" />
                <RoundTable tw="h-12 w-auto" />
                <HandshakeSquare tw="h-12 w-auto" />
                <Tester tw="h-12 w-auto" />
              </>
            }
          >
            <StaticImage src="../images/government.jpg" alt="" style={fsbgImg} layout="fullWidth" />
          </IconCardLink>
        </div>
        <div tw="lg:(col-span-1 row-span-1)">
          <IconCardLink
            to="/your-impact/virtual-fundraising"
            title="Collecte de fonds virtuelle"
            icons={
              <>
                <ComputerMoney tw="h-12 w-auto" />
                <Champagne tw="h-12 w-auto" />
                <Bike tw="h-12 w-auto" />
                <Shoe tw="h-12 w-auto" />
              </>
            }
          >
            <StaticImage src="../images/government.jpg" alt="" style={fsbgImg} layout="fullWidth" />
          </IconCardLink>
        </div>
        <div tw="lg:(col-span-2 row-span-1)">
          <IconCardLink
            to="/your-impact/community"
            title="Communauté"
            icons={
              <>
                <GradCap tw="h-12 w-auto" />
                <T1DSquare tw="h-12 w-auto" />
                <Puzzle tw="h-12 w-auto" />
                <Bear tw="h-12 w-auto" />
                <Podium tw="h-12 w-auto" />
              </>
            }
          >
            <StaticImage src="../images/bear.jpg" alt="" style={fsbgImg} layout="fullWidth" />
          </IconCardLink>
        </div>
      </div>
      <Text>
        <Link
          to="https://s3.wasabisys.com/jdrf-assets/wp-content/uploads/2021/06/2020-JDRF-FS-Final-signed-FRE.pdf"
          tw="block w-full py-4 px-6 rounded-xl inline-flex items-center justify-between text-2xl transition-colors duration-100 ease-in-out bg-blue-900 text-gray-50 hover:(bg-blue-800) md:(text-4xl)"
        >
          États financiers
        </Link>
      </Text>
    </div>
  </Layout>
);

export default IndexPage;
